/**
 * <cos-deselectable-radios> — wraps a group of radios so clicking an
 * already-selected radio (or its <label>) deselects it.
 *
 * Native HTML radios cannot be unchecked by re-clicking. This element
 * captures mousedown/keydown to remember if the target was already
 * checked, then on click it intercepts the default selection, unchecks
 * the radio, and dispatches a bubbling `change` event so listeners
 * (price totals, validation, etc.) can react.
 *
 * Label clicks work because we resolve the target radio from the
 * containing <label> via label.control / label[for] / nested input.
 *
 * Usage:
 *   <cos-deselectable-radios>
 *     <label for="r1"><input type="radio" name="g" id="r1"> One</label>
 *     <label for="r2"><input type="radio" name="g" id="r2"> Two</label>
 *   </cos-deselectable-radios>
 *
 * Keyboard: Space / Enter on a focused radio is treated the same way.
 * Arrow-key group navigation is not interfered with — those keys fire
 * `change` directly and the new radio was never `wasChecked`.
 */
class CosDeselectableRadios extends HTMLElement {
  connectedCallback() {
    this._onDown = this._onDown.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
    this.addEventListener('mousedown', this._onDown, true);
    this.addEventListener('keydown', this._onKeyDown, true);
    this.addEventListener('click', this._onClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('mousedown', this._onDown, true);
    this.removeEventListener('keydown', this._onKeyDown, true);
    this.removeEventListener('click', this._onClick, true);
  }

  _resolveRadio(event) {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.type === 'radio') return t;
    // Bail if the click landed on another interactive descendant of the
    // label (e.g. a tooltip "?" button) — those swallow their own click
    // and must not toggle the radio.
    if (t && t.closest) {
      const interactive = t.closest('button, a, select, textarea, [role="button"]');
      if (interactive) return null;
      const otherInput = t.closest('input');
      if (otherInput && otherInput !== t) return null;
    }
    const label = t && t.closest ? t.closest('label') : null;
    if (!label) return null;
    if (label.control instanceof HTMLInputElement && label.control.type === 'radio') {
      return label.control;
    }
    const forId = label.getAttribute('for');
    if (forId) {
      const ref = this.ownerDocument.getElementById(forId);
      if (ref instanceof HTMLInputElement && ref.type === 'radio') return ref;
    }
    return label.querySelector('input[type="radio"]');
  }

  _onDown(e) {
    const r = this._resolveRadio(e);
    if (!r || !this.contains(r)) return;
    r.dataset.cosWasChecked = r.checked ? '1' : '';
  }

  _onKeyDown(e) {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    const r = this._resolveRadio(e);
    if (!r || !this.contains(r)) return;
    r.dataset.cosWasChecked = r.checked ? '1' : '';
  }

  _onClick(e) {
    const r = this._resolveRadio(e);
    if (!r || !this.contains(r)) return;
    if (r.dataset.cosWasChecked === '1') {
      e.preventDefault();
      r.checked = false;
      r.dispatchEvent(new Event('change', { bubbles: true }));
    }
    r.dataset.cosWasChecked = '';
  }
}

if (!customElements.get('cos-deselectable-radios')) {
  customElements.define('cos-deselectable-radios', CosDeselectableRadios);
}
